import {Component,OnInit} from '@angular/core';
import {MotInterdit} from '@domain/admin/parametre/authentification/mot-interdit';
import {ListView,TypeComparaison} from '@domain/common/list-view';
import {TranslateService} from '@ngx-translate/core';
import {ListeNoireItemComponent} from './liste-noire-item/liste-noire-item.component';
import {BehaviorSubject} from 'rxjs';
import {FloatingButtonAction,TypeAction} from '@share/component/floating-button/floating-button';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {ListeNoireAddComponent} from './liste-noire-add/liste-noire-add.component';
import {ListeNoireHttpService} from "@services/admin/parametre/authentification/liste-noire-http.service";
import {handleResult} from "@share/utils/handle-result";
import {ToastrService} from "ngx-toastr";
import {Sorting} from "@domain/common/list-view/sorting";

/**
 * Composant d'affichage de la liste noire des mots interdits
 *
 * @author Lionel Gimenez
 * @date 26/08/2025
 */
@Component({
    host: {'data-test-id': 'liste-noire'},
    templateUrl: './liste-noire.component.html'
})
export class ListeNoireComponent implements OnInit {
    /** Liste des mots interdits */
    listeMotsInterdits: ListView<MotInterdit,ListeNoireItemComponent>;

    /** Liste des actions possibles */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /**
     * Constructeur
     *
     * @param translateService Service de traduction
     * @param matDialog Service de dialogue
     */
    constructor(
        private translateService: TranslateService,
        private matDialog: MatDialog,
        private listeNoireHttpService: ListeNoireHttpService,
        private toastrService: ToastrService,
    ) {
    }

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Initialisation de la liste des mots interdits
        this.listeMotsInterdits = new ListView<MotInterdit,ListeNoireItemComponent>({
            uri: '/controller/AuthAdmin/listeMotsBlackList',
            title: this.translateService.instant('admin.parametres.authentification.listeNoire.title'),
            component: ListeNoireItemComponent,
            isFilter: true,
            defaultOrder: 'mot',
            listeFilters: [
                {
                    clef: 'mot',
                    title: this.translateService.instant('admin.parametres.authentification.listeNoire.mot'),
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    isDefault: true
                }
            ]
        });

        //Liste des colonnes triables
        this.listeMotsInterdits.columns = [
            {key: 'mot',title: 'admin.parametres.authentification.listeNoire.mot'},
        ]

        //On initialise le tri
        this.listeMotsInterdits.sorting = new Sorting(this.listeMotsInterdits.columns,'mot');

        //Nom de la classe
        this.listeMotsInterdits.className = "ListeNoireComponent";

        //Persistence des filtres
        this.listeMotsInterdits.isPersistFilters = true;

        //Définition des actions
        this.defineListeActions();
    }

    /**
     * Définition de la liste des actions
     */
    defineListeActions(): void {
        const liste: FloatingButtonAction[] = [{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-ajouter',
            libelle: 'global.actions.creer',
            doAction: () => this.openAddDialog()
        },{
            type: TypeAction.SECONDARY,
            icone: 'nio icon-tout_selectionner',
            libelle: 'liste.actions.selectionnerElementsAffiches',
            isVisible: () => this.listeMotsInterdits.listeObjetsSectionnes?.length > 0,
            doAction: () => this.listeMotsInterdits.selectAll(true),
        },{
            type: TypeAction.SECONDARY,
            icone: 'nio icon-annuler_selection',
            libelle: 'liste.actions.toutDeselectionner',
            isVisible: () => this.listeMotsInterdits.listeObjetsSectionnes?.length > 0,
            doAction: () => this.listeMotsInterdits.selectAll(false)
        },{
            type: TypeAction.SECONDARY,
            icone: 'nio icon-suppression',
            libelle: 'global.actions.supprimer',
            doAction: () => this.supprimerMultiple(),
            isVisible: () => this.listeMotsInterdits.listeObjetsSectionnes?.length > 0,
        }];

        this.listeActions.next(liste);
    }

    /**
     * Supprimer les mots interdits sélectionnés
     */
    supprimerMultiple(): void {
        this.listeNoireHttpService
            .delete(this.listeMotsInterdits.listeObjetsSectionnes)
            .pipe(handleResult(this.translateService,this.toastrService,'global.success.suppression','global.success.supression'))
            .subscribe(result => {
                this.listeMotsInterdits.refresh();
            });
    }

    /**
     * Ouverture de la popin d'ajout
     */
    openAddDialog(): void {
        const dialogRef = this.matDialog.open(ListeNoireAddComponent,{
            width: '500px'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                //Rechargement de la liste
                this.listeMotsInterdits.refresh();
            }
        });
    }
}
